import {
  Typography,
  CircularProgress,
  Box,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
} from "@mui/material";
import { harmony_ring_program_list_api } from "src/DAL/Login/Login";
import { makeStyles } from "@mui/styles";
import { useSnackbar } from "notistack";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { s3baseUrl } from "src/config/config";
import { useNavigate } from "react-router-dom";
import RecordNotFound from "src/components/RecordNotFound";
import RecentMembersTable from "src/components/_dashboard/RecentMembers";
import { useQuery } from "@tanstack/react-query";
import TrackerAccountabilitySection from "src/pages/Trackers/TrackerAccountabilitySection";
import { fShortenNumber } from "src/utils/formatNumber";

const PRIMARY = "var(--portal-theme-primary, #04abbb)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

const StatCard = ({ label, value, hint, tone }) => (
  <Box
    sx={{
      flex: "1 1 150px",
      minWidth: 140,
      p: 1.75,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      borderLeft: tone ? `3px solid ${tone}` : `1px solid ${BORDER}`,
      bgcolor: "#fff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      textAlign: "left",
      height: "100%",
    }}
  >
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED }}>
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.5,
        fontSize: 24,
        fontWeight: 700,
        color: tone || INK,
        lineHeight: 1.15,
      }}
    >
      {value}
    </Typography>
    {hint ? (
      <Typography sx={{ mt: 0.5, fontSize: 11.5, color: MUTED, lineHeight: 1.35 }}>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

const SectionPanel = ({ title, subtitle, children }) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      maxHeight: 320,
      display: "flex",
      flexDirection: "column",
      p: 2,
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      bgcolor: "#fff",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    }}
  >
    <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
      {title}
    </Typography>
    {subtitle ? (
      <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25, mb: 1 }}>
        {subtitle}
      </Typography>
    ) : null}
    <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "hidden" }}>{children}</Box>
  </Box>
);

export default function DashboardApp() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const fetchDashboardData = async () => {
    const result = await harmony_ring_program_list_api();
    if (result.code === 200) {
      return result;
    }
    throw new Error(result.message);
  };

  const { data: inputs, isLoading } = useQuery({
    queryKey: ["dashboardData"],
    queryFn: fetchDashboardData,
    onError: (err) => {
      enqueueSnackbar(err.message, { variant: "error" });
    },
  });

  const dataProgram = inputs?.program || [];

  const handleClick = (value) => {
    navigate(`/dashboard/harmony-program/${value._id}`, {
      state: value,
    });
  };

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <div className="container">
      <div className="row">
        <div className="col-md-12 mt-4">
          <Typography className="welcome-back" variant="h4">
            Hi, Welcome back
          </Typography>
        </div>

        <div className="col-md-12 mt-3">
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
            }}
          >
            <StatCard
              label="Total Amount"
              value={`£${fShortenNumber(inputs?.total_transaction_amount)}`}
              hint="All transactions"
              tone="#04abbb"
            />
            <StatCard
              label="Total Active Members"
              value={fShortenNumber(inputs?.member_count)}
              hint="Members currently active"
              tone="#10b981"
            />
            <StatCard
              label="Total Active Subscription"
              value={fShortenNumber(inputs?.total_subscription)}
              hint="Live subscriptions"
              tone="#0ea5e9"
            />
          </Box>
        </div>

        <div className="col-md-12 mt-4">
          <TrackerAccountabilitySection />
        </div>

        <div className="col-md-12 mt-2 mb-1">
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>
            Membership &amp; programmes
          </Typography>
          <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.25 }}>
            Harmony Ring programmes and recently joined members.
          </Typography>
        </div>

        <div className="col-md-6 mt-2 mb-4 d-flex align-items-stretch">
          <SectionPanel
            title="Harmony Ring Programmes"
            subtitle="Programmes with Harmony Ring enabled"
          >
            {dataProgram && dataProgram.length > 0 ? (
              <List
                dense
                sx={{
                  overflowY: "auto",
                  height: "100%",
                  maxHeight: 230,
                  px: 0,
                  py: 0,
                }}
              >
                {dataProgram.map((program) => (
                  <ListItem
                    key={program._id}
                    onClick={() => handleClick(program)}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label="go"
                        onClick={() => handleClick(program)}
                        sx={{ color: PRIMARY }}
                      >
                        <ArrowForwardIosIcon style={{ fontSize: "16px" }} />
                      </IconButton>
                    }
                    disablePadding
                  >
                    <ListItemButton>
                      <ListItemAvatar>
                        <Avatar
                          alt={program.title}
                          src={s3baseUrl + program.program_images.thumbnail_1}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        secondaryTypographyProps={{ color: "#FF9800" }}
                        primary={program?.title}
                        secondary={`Members: ${program?.members_with_uncompleted_workouts}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              <RecordNotFound title="Programmes" class={true} />
            )}
          </SectionPanel>
        </div>

        <div className="col-md-6 mt-2 mb-4 d-flex align-items-stretch">
          <SectionPanel
            title="Recent Members"
            subtitle="Latest people who joined"
          >
            <Box sx={{ height: "100%", maxHeight: 230, overflow: "auto" }}>
              <RecentMembersTable
                embedded
                recentMember={inputs?.recent_member || []}
              />
            </Box>
          </SectionPanel>
        </div>
      </div>
    </div>
  );
}
